import { BASELINE_FIXTURE } from '../../../rcm-baseline/mockData';
import { count, fmtDate, money } from '../../../rcm-baseline/utils';
import { BarList, Buckets, Card, Metric, SectionHead } from '../shared/BaselinePrimitives';

const f = BASELINE_FIXTURE.facts;
const b = BASELINE_FIXTURE.balances;
const asOf = fmtDate(BASELINE_FIXTURE.snapshot.balancesAsOf);
const win = BASELINE_FIXTURE.snapshot.window;

export function FactsSection() {
  const whoTotal = f.whoPaid.insurers + f.whoPaid.patients;
  return (
    <section id="pb-facts" className="pb-section" aria-labelledby="pb-facts-h">
      <SectionHead num={1} title="Facts" sub={<span id="pb-facts-h">{fmtDate(win.from)} – {fmtDate(win.to)}</span>} />

      {/* Four headline cards */}
      <div className="pb-grid pb-grid-4">
        <Card label="Production posted" basis="counted">
          <Metric id="facts.production_posted" value={f.productionPosted} unit="usd" size="xl" prefix="Production posted" />
        </Card>
        <Card label="Write-offs & adjustments" basis="counted" note={`Contract ${money(f.writeOffs.contract)} · Other ${money(f.writeOffs.other)}`}>
          <Metric id="facts.write_offs" value={f.writeOffs.total} unit="usd" size="xl" prefix="Write-offs and adjustments" />
        </Card>
        <Card label="Production after write-offs" basis="counted">
          <Metric id="facts.production_after" value={f.productionAfterWriteOffs} unit="usd" size="xl" prefix="Production after write-offs" />
        </Card>
        <Card label="Collections posted" basis="counted">
          <Metric id="facts.collections_posted" value={f.collectionsPosted} unit="usd" size="xl" prefix="Collections posted" />
        </Card>
      </div>

      {/* Three secondary cards */}
      <div className="pb-grid pb-grid-3" style={{ marginTop: 'var(--pb-gap)' }}>
        <Card label="Collected ÷ production after write-offs" basis="counted" note="Includes payments for work done in earlier periods.">
          <Metric id="facts.collected_ratio" value={f.collectedRatio} unit="pct" size="lg" prefix="Collected ratio" />
        </Card>
        <Card label="Patients seen" basis="counted" note="At least one completed visit in the window.">
          <Metric id="facts.patients_seen" value={f.patientsSeen} unit="count" size="lg" prefix="Patients seen" />
        </Card>
        <Card label="Claims sent" basis="counted" note={`Primary ${count(f.claimsSent.primary)} · Secondary ${count(f.claimsSent.secondary)} · Pre-auths ${count(f.claimsSent.preauthsExcluded)} counted apart`}>
          <Metric id="facts.claims_sent" value={f.claimsSent.total} unit="count" size="lg" prefix="Claims sent" />
        </Card>
      </div>

      {/* Who paid */}
      <div style={{ marginTop: 'var(--pb-gap)' }}>
        <Card label="Who paid the collections" basis="counted">
          <div className="pb-splitbar" style={{ marginTop: 4 }}>
            <div className="pb-splitbar-seg alt" style={{ flexGrow: f.whoPaid.insurers }}>
              Insurers {money(f.whoPaid.insurers)}
            </div>
            <div className="pb-splitbar-seg alt2" style={{ flexGrow: f.whoPaid.patients }}>
              Patients {money(f.whoPaid.patients)}
            </div>
          </div>
          <div className="pb-card-sub">
            <Metric id="facts.who_paid" value={whoTotal} unit="usd" size="md" prefix="Total collected" underline={false} />
          </div>
        </Card>
      </div>

      {/* Insurer payments */}
      <div style={{ marginTop: 'var(--pb-gap)' }}>
        <Card label="Insurer payments by insurer (top 5, rest grouped)" basis="counted">
          <div style={{ marginTop: 8 }}>
            <BarList
              rows={f.insurerPayments.map((p) => ({
                label: p.payer,
                value: p.amount,
                soft: p.grouped ?? false,
                display: money(p.amount),
              }))}
            />
          </div>
          <div className="pb-card-sub">
            <Metric id="facts.insurer_payments" value={f.whoPaid.insurers} unit="usd" size="md" prefix="All insurer payments" underline={false} />
          </div>
        </Card>
      </div>

      {/* Balances */}
      <div className="pb-section-head" style={{ marginTop: 28, marginBottom: 14 }}>
        <h3 className="pb-section-title" style={{ fontSize: 15 }}>Balances on {asOf}</h3>
      </div>
      <div className="pb-grid pb-grid-2">
        <Card label="Owed by insurers on sent claims" basis="counted" note="Aged from the date each claim was first sent.">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <Metric id="balances.owed_insurers" value={b.insurers.total} unit="usd" size="lg" prefix="Owed by insurers" />
            <span style={{ fontSize: 12, color: 'var(--color-muted-foreground)' }}>{b.insurers.claims} claims</span>
          </div>
          <Buckets buckets={b.insurers.buckets} />
        </Card>
        <Card label="Owed by patients" basis="counted" note="Shown in the PMS's own aging buckets, never relabelled.">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <Metric id="balances.owed_patients" value={b.patients.total} unit="usd" size="lg" prefix="Owed by patients" />
            <span style={{ fontSize: 12, color: 'var(--color-muted-foreground)' }}>{b.patients.accounts} accounts</span>
          </div>
          <Buckets buckets={b.patients.buckets} />
        </Card>
      </div>
      <div className="pb-grid pb-grid-2" style={{ marginTop: 'var(--pb-gap)' }}>
        <Card label="Credit balances" basis="counted" note="On its own. Never subtracted from what is owed.">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <Metric id="balances.credit_balances" value={b.creditBalances.total} unit="usd" size="md" prefix="Credit balances" />
            <span style={{ fontSize: 12, color: 'var(--color-muted-foreground)' }}>{b.creditBalances.accounts} accounts</span>
          </div>
        </Card>
        <Card label="Payments not applied to any visit" basis="counted" note="On its own. Never subtracted from what is owed.">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <Metric id="balances.unapplied" value={b.unappliedPayments.total} unit="usd" size="md" prefix="Unapplied payments" />
            <span style={{ fontSize: 12, color: 'var(--color-muted-foreground)' }}>{b.unappliedPayments.payments} payments</span>
          </div>
        </Card>
      </div>
    </section>
  );
}
